import { useCallback, useEffect, useMemo, useState, type ReactNode } from "react";
import { FormattedMessage, useIntl } from "react-intl";

// Shared Grow assessment template, extracted from the self-assessment design
// (ElementAssessmentPage): progress boxes, one item in focus, an option grid,
// three keyboard zones (boxes / rating / nav) marked by a left-gutter chevron,
// and the completion toast. Wrappers own data and persistence: they map their
// statements/questions to items, hold the selection map and decide what
// submit means (evaluation page, mutation, navigation).

export type AssessmentOption = {
  key: string;
  // Big text on the tile (Likert level, choice number) + small label under it.
  title: ReactNode;
  label: ReactNode;
};

export type AssessmentItem = {
  id: string;
  // Display-ready statement/question (wrappers add quotes if wanted).
  text: string;
  options: readonly AssessmentOption[];
};

type Zone = "boxes" | "rating" | "nav";
type NavSide = "back" | "next";

type Props = {
  items: readonly AssessmentItem[];
  // itemId → chosen option key.
  selected: Readonly<Record<string, string | undefined>>;
  onSelect: (itemId: string, optionKey: string) => void;
  // Label over the focused item ("Statement {n}" / "Question {n}"), 1-based.
  itemLabel: (index: number) => ReactNode;
  // Rendered after the progress text (e.g. the autosave status).
  statusSuffix?: ReactNode;
  saving: boolean;
  saveError: boolean;
  submitLabel: ReactNode;
  submitTitle?: string;
  onSubmit: () => void;
  // Optional: when set, an assistant icon shows next to the focused item; clicking it calls this with
  // the focused item (wrappers wire it to open the Assistant to help evaluate that statement).
  onAskAboutItem?: (item: AssessmentItem) => void;
};

export function AssessmentFlow({
  items,
  selected,
  onSelect,
  itemLabel,
  statusSuffix,
  saving,
  saveError,
  submitLabel,
  submitTitle,
  onSubmit,
  onAskAboutItem,
}: Props) {
  const intl = useIntl();

  const [current, setCurrent] = useState(0);
  // Drives the one-shot "pop" on the freshly picked option (n re-fires it).
  const [picked, setPicked] = useState<{ id: string; key: string; n: number } | null>(null);
  // Active keyboard zone + which nav button is selected within the nav zone.
  const [zone, setZone] = useState<Zone>("rating");
  const [navSide, setNavSide] = useState<NavSide>("next");
  // Item ids whose assistant icon has been clicked → show the icon in its "active" state (mirrors the
  // global AssistantLauncher's open look: flipped mascot + filled/ringed background).
  const [askedIds, setAskedIds] = useState<ReadonlySet<string>>(() => new Set());

  const total = items.length;
  const safeIndex = total > 0 ? Math.min(current, total - 1) : 0;
  const cur = items[safeIndex];
  const options = useMemo(() => cur?.options ?? [], [cur]);
  const hasNext = safeIndex < total - 1;
  const answeredCount = items.reduce((n, it) => (selected[it.id] !== undefined ? n + 1 : n), 0);
  const complete = total > 0 && answeredCount === total;
  // Where ←/→ starts on an unanswered item: the middle option (Likert 3 of 5).
  const midIndex = Math.floor((options.length - 1) / 2);

  const pick = useCallback(
    (optionKey: string) => {
      if (!cur) return;
      // No auto-advance: the selection stays visible and the user moves on with
      // "Weiter" (auto-jumping felt like the answer wasn't registered).
      onSelect(cur.id, optionKey);
      setPicked((p) => ({ id: cur.id, key: optionKey, n: (p?.n ?? 0) + 1 }));
    },
    [cur, onSelect],
  );

  const goNext = useCallback(() => setCurrent((c) => Math.min(total - 1, c + 1)), [total]);
  const goBack = useCallback(() => setCurrent((c) => Math.max(0, c - 1)), []);

  // Keyboard. ↑/↓ cycle the zones (boxes ↔ rating ↔ nav). ←/→ acts in the active
  // zone: browse questions (boxes), step the rating, or toggle Zurück/Weiter
  // (nav). 1-9 picks directly; Enter activates. "a"/"d"/"w"/"s" mirror the
  // arrows as an intentional, undocumented gamer feature (WASD-style).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (!cur) return;
      const tag = (e.target as HTMLElement | null)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      const selectedIdx = options.findIndex((o) => o.key === selected[cur.id]);

      if (e.key >= "1" && e.key <= "9") {
        const option = options[Number(e.key) - 1];
        if (option) {
          pick(option.key);
          setZone("rating");
          e.preventDefault();
        }
      } else if (e.key === "ArrowUp" || e.key === "w" || e.key === "W") {
        setZone((z) => (z === "nav" ? "rating" : "boxes"));
        e.preventDefault();
      } else if (e.key === "ArrowDown" || e.key === "s" || e.key === "S") {
        setZone((z) => {
          if (z === "boxes") return "rating";
          if (z === "rating") {
            setNavSide(hasNext ? "next" : "back");
            return "nav";
          }
          return "nav";
        });
        e.preventDefault();
      } else if (
        e.key === "ArrowRight" ||
        e.key === "ArrowLeft" ||
        e.key === "a" ||
        e.key === "A" ||
        e.key === "d" ||
        e.key === "D"
      ) {
        const right = e.key === "ArrowRight" || e.key === "d" || e.key === "D";
        if (zone === "boxes") {
          setCurrent((c) => (right ? Math.min(total - 1, c + 1) : Math.max(0, c - 1)));
        } else if (zone === "nav") {
          setNavSide(right ? (hasNext ? "next" : "back") : "back");
        } else {
          // Rating: start in the middle on the first key, then step.
          const next =
            selectedIdx < 0
              ? midIndex
              : right
                ? Math.min(options.length - 1, selectedIdx + 1)
                : Math.max(0, selectedIdx - 1);
          if (options[next]) pick(options[next].key);
        }
        e.preventDefault();
      } else if (e.key === "Enter") {
        if (zone === "boxes") {
          setZone("rating");
        } else if (zone === "nav") {
          if (navSide === "next") goNext();
          else goBack();
          setZone("rating");
        } else if (selected[cur.id] !== undefined) {
          goNext();
        }
        e.preventDefault();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cur, options, selected, zone, navSide, hasNext, total, midIndex, pick, goNext, goBack]);

  // Quiet pill (like Save·Service "Bearbeiten") when not active; filled
  // smycles-button when keyboard-selected in the nav zone.
  const navClass = (active: boolean) =>
    active
      ? "smycles-button px-5 py-2.5 text-[11px]"
      : "rounded-full px-5 py-2.5 text-[11px] text-ink/80 ring-1 ring-black/15 transition hover:text-ink hover:ring-black/25 disabled:opacity-40";

  // Left-gutter chevron pointing at the active zone's row.
  const chevron = (active: boolean) =>
    active ? (
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5 text-accent"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="m9 6 6 6-6 6" />
      </svg>
    ) : null;

  return (
    <>
      <div className="space-y-6 pb-24">
        {/* The chevron sits absolutely in the left margin (right-full) so the
            content stays left-aligned with the header; only the chevron pokes
            further left. It marks the active keyboard zone and moves with ↑/↓. */}
        {/* Boxes zone: one box per question - filled when answered, so skipped
            questions are visible. Click a box (or ←/→ in the boxes zone) to jump. */}
        <div>
          <div className="text-[11px] text-ink/55">
            <FormattedMessage
              id="grow.assessment.progress"
              defaultMessage="Question {index} / {total} · {answered} answered"
              values={{ index: safeIndex + 1, total, answered: answeredCount }}
            />
            {statusSuffix}
          </div>
          <div className="relative mt-2 flex flex-wrap gap-1">
            <span className="absolute right-full top-1/2 mr-12 -translate-y-1/2">
              {chevron(zone === "boxes")}
            </span>
            {items.map((it, i) => {
              const answered = selected[it.id] !== undefined;
              const isCurrent = i === safeIndex;
              return (
                <button
                  key={it.id}
                  type="button"
                  onClick={() => {
                    setCurrent(i);
                    setZone("rating");
                  }}
                  title={
                    answered
                      ? intl.formatMessage(
                          {
                            id: "grow.assessment.boxTitleAnswered",
                            defaultMessage: "Question {index} · answered",
                          },
                          { index: i + 1 },
                        )
                      : intl.formatMessage(
                          { id: "grow.assessment.boxTitle", defaultMessage: "Question {index}" },
                          { index: i + 1 },
                        )
                  }
                  aria-label={intl.formatMessage(
                    { id: "grow.assessment.boxAriaLabel", defaultMessage: "Go to question {index}" },
                    { index: i + 1 },
                  )}
                  className={`h-2.5 min-w-[10px] flex-1 rounded-sm transition ${
                    answered ? "bg-accent" : "bg-ink/15"
                  } ${
                    isCurrent
                      ? zone === "boxes"
                        ? "ring-2 ring-accent"
                        : "ring-1 ring-ink"
                      : ""
                  }`}
                />
              );
            })}
          </div>
        </div>

        {/* Rating zone */}
        <div className="min-h-[200px]">
          <div className="field-label text-ink/45">{itemLabel(safeIndex + 1)}</div>
          <div className="mt-2 flex items-start gap-2">
            <p className="font-serif text-2xl leading-snug">{cur?.text ?? ""}</p>
            {onAskAboutItem && cur ? (
              <button
                type="button"
                onClick={() => {
                  setAskedIds((prev) => new Set(prev).add(cur.id));
                  onAskAboutItem(cur);
                }}
                aria-label={intl.formatMessage({ id: "grow.assessment.askAria" })}
                aria-pressed={askedIds.has(cur.id)}
                title={intl.formatMessage({ id: "grow.assessment.askAria" })}
                // Exactly the global AssistantLauncher states: resting = AI shadow on page bg; active
                // (once clicked) = filled/ringed background.
                className={`grid h-9 w-9 shrink-0 place-items-center rounded-full transition hover:bg-ink/[0.06] ${
                  askedIds.has(cur.id) ? "bg-ink/[0.06] ring-1 ring-ink/15" : "smycles-ai-shadow bg-page"
                }`}
              >
                {/* Faces left resting; flips to face the chat on the right when active (like the launcher). */}
                <img
                  src="/assistant/smike.svg"
                  alt=""
                  aria-hidden
                  draggable={false}
                  className={`h-6 w-6 select-none transition-transform duration-200 motion-reduce:transition-none ${
                    askedIds.has(cur.id) ? "-scale-x-100" : ""
                  }`}
                />
              </button>
            ) : null}
          </div>

          <div
            role="radiogroup"
            aria-label={cur?.text ?? ""}
            className="relative mt-6 grid gap-2"
            style={{ gridTemplateColumns: `repeat(${Math.max(options.length, 1)}, minmax(0, 1fr))` }}
          >
            <span className="absolute right-full top-1/2 mr-12 -translate-y-1/2">
              {chevron(zone === "rating")}
            </span>
            {options.map((o, i) => {
              const isSelected = cur ? selected[cur.id] === o.key : false;
              const unanswered = !!cur && selected[cur.id] === undefined;
              // On an unanswered question, frame the middle option: that's where
              // ←/→ starts (default), so the user sees where the pop will land.
              const defaultStart = !isSelected && unanswered && i === midIndex;
              // Re-keying the just-picked tile remounts it so the pop replays
              // on every (re)selection of the same option.
              const justPicked = !!cur && picked?.id === cur.id && picked?.key === o.key;
              return (
                <button
                  key={justPicked ? `${o.key}#${picked.n}` : o.key}
                  type="button"
                  role="radio"
                  aria-checked={isSelected}
                  onClick={() => pick(o.key)}
                  className={`flex min-h-[80px] flex-col items-center justify-center gap-1.5 rounded-lg px-1 py-3 text-center ring-1 transition focus:outline-none ${
                    justPicked ? "answer-pop" : ""
                  } ${
                    isSelected
                      ? "bg-ink text-on-ink ring-ink"
                      : `bg-white text-ink/70 hover:text-ink hover:ring-accent ${
                          defaultStart ? "ring-2 ring-accent/60" : "ring-black/15"
                        }`
                  }`}
                >
                  <span className="text-base font-medium leading-none">{o.title}</span>
                  <span className="text-[9px] leading-tight tracking-wide">{o.label}</span>
                </button>
              );
            })}
          </div>
          <p className="mt-3 text-center text-[12px] text-ink">
            <FormattedMessage
              id="grow.assessment.keyboardHint"
              defaultMessage="Keys 1–{max} or ←/→ to select"
              values={{ max: Math.min(9, options.length) }}
            />
          </p>
        </div>

        {/* Nav zone */}
        <div>
          <div className="relative flex min-h-9 items-center justify-between gap-3">
            <span className="absolute right-full top-1/2 mr-12 -translate-y-1/2">
              {chevron(zone === "nav")}
            </span>
            <button
              type="button"
              onClick={goBack}
              disabled={safeIndex === 0}
              className={navClass(zone === "nav" && navSide === "back")}
            >
              <FormattedMessage id="common.back" defaultMessage="Back" />
            </button>
            {hasNext ? (
              <button
                type="button"
                onClick={goNext}
                className={navClass(zone === "nav" && navSide === "next")}
              >
                {cur && selected[cur.id] !== undefined ? (
                  <FormattedMessage id="common.next" defaultMessage="Next" />
                ) : (
                  <FormattedMessage id="grow.assessment.skip" defaultMessage="Skip" />
                )}
              </button>
            ) : null}
          </div>
          {hasNext && cur && selected[cur.id] !== undefined ? (
            <div className="mt-1 text-right text-[10px] text-ink/45">
              <FormattedMessage id="grow.assessment.enterToContinue" defaultMessage="Enter to continue" />
            </div>
          ) : null}
        </div>

        {/* ↑/↓ zone-switch hint, below the rows. */}
        <div className="text-[11px] text-ink/45">
          <FormattedMessage
            id="grow.assessment.zoneHint"
            defaultMessage="↑/↓ switches the row (Questions · Rating · Navigation)"
          />
        </div>
      </div>

      {complete ? (
        <div className="fixed bottom-4 sm:bottom-8 left-1/2 z-[900] w-full max-w-lg -translate-x-1/2 px-4">
          <div className="smycles-toast pr-2">
            <span className="min-w-0 flex-1 text-sm">
              {saveError ? (
                <span className="font-medium">
                  <FormattedMessage id="grow.assessment.saveFailed" defaultMessage="Could not save." />
                </span>
              ) : (
                <FormattedMessage id="grow.assessment.allAnswered" defaultMessage="All questions answered" />
              )}
            </span>
            <button
              type="button"
              onClick={onSubmit}
              aria-disabled={saving}
              title={submitTitle}
              className={`smycles-button px-5 py-1.5 text-[12px] ${
                saving ? "cursor-not-allowed opacity-50" : ""
              }`}
            >
              {saving ? (
                <FormattedMessage id="common.saving" defaultMessage="Saving…" />
              ) : (
                submitLabel
              )}
            </button>
          </div>
        </div>
      ) : null}
    </>
  );
}
